import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isObject } from "@/shared/utils/typeGuards";

export const kAceStepRoute = "/ace-step";

export const kAceStepTaskTypes = [
    "text2music",
    "cover",
    "repaint",
    "extract",
    "lego",
    "complete",
] as const;
export type AceStepTaskType = (typeof kAceStepTaskTypes)[number];

export const kAceStepVocalLanguages = ["en", "zh", "ja", "unknown"] as const;
export type AceStepVocalLanguage = (typeof kAceStepVocalLanguages)[number];

export const kAceStepAudioFormats = ["wav", "mp3", "flac"] as const;
export type AceStepAudioFormat = (typeof kAceStepAudioFormats)[number];

export const kAceStepTimeSignatures = ["2", "3", "4", "6"] as const;
export type AceStepTimeSignature = (typeof kAceStepTimeSignatures)[number];

export const kAceStepBpmRange = { min: 30, max: 300 };
export const kAceStepDurationRange = { min: 10, max: 600 };
export const kAceStepAudioCoverStrengthRange = { min: 0, max: 1 };
export const kAceStepInferenceStepsRange = { min: 1, max: 100 };
export const kAceStepGuidanceScaleRange = { min: 0, max: 20 };
export const kAceStepBatchSizeRange = { min: 1, max: 8 };
export const kAceStepCaptionMaxLength = 512;
export const kAceStepLyricsMaxLength = 4096;

export interface AceStepRequestBody {
    // Optional file inputs
    referenceAudioKey?: string;
    sourceAudioKey?: string;
    // Config
    taskType?: AceStepTaskType;
    caption?: string;
    lyrics?: string;
    instrumental?: boolean;
    bpm?: number;
    keyscale?: string;
    timesignature?: AceStepTimeSignature;
    vocalLanguage?: AceStepVocalLanguage;
    duration?: number;
    repaintingStart?: number;
    repaintingEnd?: number;
    audioCoverStrength?: number;
    instruction?: string;
    inferenceSteps?: number;
    guidanceScale?: number;
    seed?: number;
    thinking?: boolean;
    batchSize?: number;
    audioFormat?: AceStepAudioFormat;
}

export const isAceStepRequestBody = (value: unknown): value is AceStepRequestBody =>
    isObject(value);

export interface AceStepSuccessResponse {
    ok: true;
    outputFileUrls: string[];
}

export const isAceStepSuccessResponse = (value: unknown): value is AceStepSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    Array.isArray(value.outputFileUrls) &&
    (value.outputFileUrls as unknown[]).every((u) => typeof u === "string");

export const kAceStepEndpoint: ApiEndpoint<AceStepRequestBody, AceStepSuccessResponse> = {
    route: kAceStepRoute,
    label: "ACE-Step",
    source: "Atlantis",
    category: "3P GenAI",
    description:
        "Generates and transforms music using the ACE-Step open-source diffusion model with text and lyric conditioning.",
    isRequestBody: isAceStepRequestBody,
    isSuccessResponse: isAceStepSuccessResponse,
};
